// WHAT: Custom hook for managing workspace expansion/collapse state
// WHY:  Encapsulates expansion logic for better UX with large workspace lists
// DOMAIN: Settings → TimeTracQ → Workspace Management
// DEPENDENCIES: React useState
// RISKS:
//   - NOTE: Expansion state is not persisted across sessions

import type { WorkspaceConfig } from "../types";

import { useState, useCallback } from "react";

/**
 * WHAT:
 *   Custom hook for managing workspace expansion state.
 *
 * WHY:
 *   Provides centralized state management for expand/collapse functionality.
 *
 * PARAMETERS:
 *   - config: Current workspace configuration
 *
 * RETURNS:
 *   Object containing expansion state and toggle functions.
 *
 * @returns {object} Object with `expandedWorkspaces`, `expandWorkspace`, `toggleAllWorkspaces`, `toggleWorkspaceExpansion`
 *
 * @param config
 *
 */
export function useWorkspaceExpansion(config: null | WorkspaceConfig) {
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<Set<string>>(new Set());

  /**
   * WHAT:
   *   Toggles expansion state for a specific workspace.
   *
   * WHY:
   *   Allows users to expand/collapse individual workspaces to manage view complexity.
   *
   * PARAMETERS:
   *   - workspaceName: Name of workspace to toggle
   */
  const toggleWorkspaceExpansion = useCallback((workspaceName: string) => {
    setExpandedWorkspaces((previous) => {
      const newSet = new Set(previous);
      if (newSet.has(workspaceName)) {
        newSet.delete(workspaceName);
      } else {
        newSet.add(workspaceName);
      }
      return newSet;
    });
  }, []);

  /**
   * WHAT:
   *   Toggles expansion state for all workspaces with more than 4 aspects.
   *
   * WHY:
   *   Provides quick way to expand/collapse all large workspaces at once.
   */
  const toggleAllWorkspaces = useCallback(() => {
    if (!config?.workspaces) {
      return;
    }

    const workspacesWithMoreThan4Aspects = Object.keys(config.workspaces).filter(
      (workspaceName) => Object.keys(config.workspaces[workspaceName] ?? {}).length > 4,
    );

    if (expandedWorkspaces.size === workspacesWithMoreThan4Aspects.length) {
      setExpandedWorkspaces(new Set());
    } else {
      setExpandedWorkspaces(new Set(workspacesWithMoreThan4Aspects));
    }
  }, [config, expandedWorkspaces]);

  const expandWorkspace = useCallback((workspaceName: string) => {
    setExpandedWorkspaces((previous) => {
      const newSet = new Set(previous);
      newSet.add(workspaceName);
      return newSet;
    });
  }, []);

  return {
    expandedWorkspaces,
    expandWorkspace,
    toggleAllWorkspaces,
    toggleWorkspaceExpansion,
  };
}
